import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Bot, RefreshCw, Send, Sparkles, ThumbsDown, ThumbsUp, X } from "lucide-react";
import {
  teacherAssistantService,
} from "../api/teacherAssistant";
import type { LearningPlan, TeacherAssistantAction, TeacherAssistantMode, TeacherRecommendation } from "../api/teacherAssistant";
import "./TeacherAssistantPanel.css";

type AssistantBubble = {
  id: string;
  role: "assistant" | "user";
  content: string;
  recommendations?: TeacherRecommendation[];
  learningPlan?: LearningPlan | null;
  action?: TeacherAssistantAction;
  messageId?: string;
  feedbackState?: "idle" | "commenting" | "saving" | "leaving" | "done";
  feedbackComment?: string;
  feedbackError?: string;
};

const INITIAL_CHAT_REQUEST =
  "Проанализируй текущий диалог и подбери наиболее подходящего преподавателя";

const QUICK_ACTIONS: Array<{ action: TeacherAssistantAction; label: string; message: string; curricula: boolean }> = [
  { action: "learning_plan", label: "Создать план обучения", message: "Создай персональный план обучения по запросу клиента", curricula: true },
  { action: "program_match", label: "Подобрать программу", message: "Подбери подходящую программу обучения INNOPROG", curricula: true },
  { action: "clarify_request", label: "Что уточнить у клиента?", message: "Какие вопросы нужно уточнить у клиента?", curricula: false },
  { action: "draft_reply", label: "Подготовить ответ клиенту", message: "Подготовь черновик ответа клиенту", curricula: false },
];

const createIdempotencyKey = () =>
  window.crypto?.randomUUID?.() ||
  `assistant-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const PANEL_WIDTH_KEY = "innoprog-teacher-assistant-width";
const DEFAULT_PANEL_WIDTH = 480;
const clampPanelWidth = (value: number) =>
  Math.max(360, Math.min(value, 1200, window.innerWidth * 0.9));

export function TeacherAssistantPanel({
  mode,
  chatId,
  chatName,
  curriculaReady = false,
  onClose,
}: {
  mode: TeacherAssistantMode;
  chatId?: string;
  chatName?: string;
  curriculaReady?: boolean;
  onClose: () => void;
}) {
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState<AssistantBubble[]>(
    mode === "standalone"
      ? [
          {
            id: "welcome",
            role: "assistant",
            content:
              "Опишите ученика, его уровень, цель и желаемое направление — я подберу подходящих преподавателей из базы INNOPROG",
          },
        ]
      : [],
  );
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [panelWidth, setPanelWidth] = useState(() => {
    const stored = Number(window.localStorage.getItem(PANEL_WIDTH_KEY));
    return clampPanelWidth(Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_PANEL_WIDTH);
  });
  const [resizing, setResizing] = useState(false);
  const resizingRef = useRef(false);
  const panelWidthRef = useRef(panelWidth);
  const sessionRef = useRef("");
  const mountedRef = useRef(true);
  const sessionStorageKey = `innoprog-teacher-assistant-session:${mode}:${chatId || "standalone"}`;
  const idempotencyRef = useRef(
    window.localStorage.getItem(sessionStorageKey) || createIdempotencyKey(),
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastRequestRef = useRef<{ message: string; action?: TeacherAssistantAction } | null>(null);

  const closeAndDelete = useCallback(() => {
    const activeSession = sessionRef.current;
    window.localStorage.removeItem(sessionStorageKey);
    if (activeSession) {
      void teacherAssistantService
        .remove(activeSession, mode === "chat" ? chatId : undefined)
        .catch(() => undefined);
    }
    onClose();
  }, [chatId, mode, onClose, sessionStorageKey]);

  const append = (role: AssistantBubble["role"], content: string, recommendations?: TeacherRecommendation[], messageId?: string, learningPlan?: LearningPlan | null, action?: TeacherAssistantAction) =>
    setMessages((current) => [
      ...current,
      { id: `${role}-${Date.now()}-${Math.random()}`, role, content, recommendations, messageId, learningPlan, action, feedbackState: messageId ? "idle" : undefined },
    ]);

  const updateMessage = (id: string, patch: Partial<AssistantBubble>) =>
    setMessages((current) => current.map((message) => message.id === id ? { ...message, ...patch } : message));

  const ask = async (activeSessionId: string, message: string, action?: TeacherAssistantAction) => {
    lastRequestRef.current = { message, action };
    setBusy(true);
    setError("");
    try {
      const response = action
        ? await teacherAssistantService.send(activeSessionId, message, mode === "chat" ? chatId : undefined, action)
        : await teacherAssistantService.send(activeSessionId, message, mode === "chat" ? chatId : undefined);
      if (mountedRef.current)
        append(
          "assistant",
          response.answer,
          response.result?.recommendations?.slice(0, 3),
          response.message_id,
          response.result?.learning_plan,
          action,
        );
    } catch (cause: any) {
      if (mountedRef.current)
        setError(
          cause.response?.data?.detail ||
            "Не удалось получить ответ. Попробуйте ещё раз",
        );
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  useEffect(() => {
    window.localStorage.setItem(sessionStorageKey, idempotencyRef.current);
    mountedRef.current = true;
    let cancelled = false;
    const start = async () => {
      setBusy(true);
      setError("");
      try {
        const session = await teacherAssistantService.create(
          mode,
          idempotencyRef.current,
          chatId,
        );
        if (cancelled) return;
        sessionRef.current = session.session_id;
        setSessionId(session.session_id);
        const restored = (session.history || []).map((message, index) => ({
          id: `restored-${index}-${message.message_id || message.role}`,
          role: message.role,
          content: message.content,
          recommendations: message.recommendations?.slice(0, 3),
          learningPlan: message.learning_plan,
          messageId: message.message_id || undefined,
          feedbackState: message.message_id ? (message.feedback_rating ? "done" as const : "idle" as const) : undefined,
        }));
        if (restored.length) {
          setMessages(restored);
          setBusy(false);
          requestAnimationFrame(() => inputRef.current?.focus());
        } else if (mode === "chat") {
          append("user", INITIAL_CHAT_REQUEST);
          await ask(session.session_id, INITIAL_CHAT_REQUEST);
        } else {
          setMessages([{
            id: "welcome",
            role: "assistant",
            content: "Опишите ученика, его уровень, цель и желаемое направление — я подберу подходящих преподавателей из базы INNOPROG",
          }]);
          setBusy(false);
          requestAnimationFrame(() => inputRef.current?.focus());
        }
      } catch (cause: any) {
        if (mountedRef.current) {
          setBusy(false);
          setError(
            cause.response?.data?.detail ||
              "Не удалось открыть AI-помощника",
          );
        }
      }
    };
    void start();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
    // A panel instance is permanently bound to the mode/chat used at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messages, busy]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeAndDelete();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [closeAndDelete]);

  useEffect(() => {
    const onResize = () => setPanelWidth((current) => {
      const next = clampPanelWidth(current);
      panelWidthRef.current = next;
      return next;
    });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      resizingRef.current = false;
      document.body.classList.remove("teacher-assistant-resizing");
    };
  }, []);

  const saveWidth = (value: number) => {
    const next = clampPanelWidth(value);
    panelWidthRef.current = next;
    setPanelWidth(next);
    window.localStorage.setItem(PANEL_WIDTH_KEY, String(Math.round(next)));
  };

  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (window.matchMedia("(max-width: 700px)").matches) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizingRef.current = true;
    setResizing(true);
    document.body.classList.add("teacher-assistant-resizing");
  };

  const moveResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!resizingRef.current) return;
    const next = clampPanelWidth(window.innerWidth - event.clientX);
    panelWidthRef.current = next;
    setPanelWidth(next);
  };

  const stopResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!resizingRef.current) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    resizingRef.current = false;
    setResizing(false);
    document.body.classList.remove("teacher-assistant-resizing");
    window.localStorage.setItem(PANEL_WIDTH_KEY, String(Math.round(panelWidthRef.current)));
  };

  const sendFeedback = async (message: AssistantBubble, rating: "up" | "down") => {
    if (!sessionId || !message.messageId || message.feedbackState === "saving") return;
    const comment = (message.feedbackComment || "").trim();
    if (rating === "down" && comment.length < 3) {
      updateMessage(message.id, { feedbackError: "Добавьте короткий комментарий" });
      return;
    }
    updateMessage(message.id, { feedbackState: "saving", feedbackError: "" });
    try {
      await teacherAssistantService.feedback(sessionId, message.messageId, rating, comment, mode === "chat" ? chatId : undefined);
      updateMessage(message.id, { feedbackState: "leaving" });
      window.setTimeout(() => {
        if (mountedRef.current) updateMessage(message.id, { feedbackState: "done" });
      }, 220);
    } catch (cause: any) {
      updateMessage(message.id, {
        feedbackState: rating === "down" ? "commenting" : "idle",
        feedbackError: cause.response?.data?.detail || "Не удалось сохранить оценку",
      });
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value || !sessionId || busy) return;
    setDraft("");
    append("user", value);
    await ask(sessionId, value);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const retry = async () => {
    if (!sessionId || busy) return;
    const request = lastRequestRef.current;
    if (request) await ask(sessionId, request.message, request.action);
  };

  const runQuickAction = async (action: TeacherAssistantAction, message: string) => {
    if (!sessionId || busy) return;
    append("user", message, undefined, undefined, undefined, action);
    await ask(sessionId, message, action);
  };

  return (
    <div className="teacher-assistant-overlay" onMouseDown={closeAndDelete}>
      <section
        className="teacher-assistant-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="teacher-assistant-title"
        onMouseDown={(event) => event.stopPropagation()}
        style={{ width: panelWidth }}
      >
        <div
          className={`teacher-assistant-resizer${resizing ? " active" : ""}`}
          role="separator"
          aria-label="Изменить ширину AI-помощника"
          aria-orientation="vertical"
          aria-valuemin={360}
          aria-valuemax={Math.round(Math.min(1200, window.innerWidth * 0.9))}
          aria-valuenow={Math.round(panelWidth)}
          tabIndex={0}
          onPointerDown={startResize}
          onPointerMove={moveResize}
          onPointerUp={stopResize}
          onPointerCancel={stopResize}
          onLostPointerCapture={(event) => stopResize(event)}
          onDoubleClick={() => saveWidth(DEFAULT_PANEL_WIDTH)}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
              event.preventDefault();
              saveWidth(panelWidth + (event.key === "ArrowLeft" ? 24 : -24));
            }
          }}
        />
        <header className="teacher-assistant-header">
          <span className="teacher-assistant-heading-icon" aria-hidden="true">
            {mode === "chat" ? <Bot size={20} /> : <Sparkles size={20} />}
          </span>
          <div>
            <h3 id="teacher-assistant-title">AI-помощник по преподавателям</h3>
            <p>
              {mode === "chat"
                ? `Диалог${chatName ? `: ${chatName}` : ""}`
                : "Подбор по вашему описанию"}
            </p>
          </div>
          <button type="button" onClick={closeAndDelete} aria-label="Закрыть AI-помощника">
            <X size={20} />
          </button>
        </header>

        <div className="teacher-assistant-messages" aria-live="polite">
          {messages.map((message) => (
            <article
              key={message.id}
              className={`teacher-assistant-message ${message.role}`}
            >
              {message.role === "assistant" && message.recommendations?.length ? (
                <div className="teacher-assistant-recommendations">
                  {message.recommendations.map((recommendation, index) => (
                    <div className="teacher-assistant-recommendation" key={`${message.id}-${recommendation.teacher}`}>
                      <strong>
                        {index + 1}. {recommendation.teacher} — {Number(recommendation.score).toFixed(1)}/10
                      </strong>
                      <p>{recommendation.reason}</p>
                    </div>
                  ))}
                </div>
              ) : message.role === "assistant" && message.learningPlan ? (
                <div className="teacher-assistant-learning-plan">
                  <h4>{message.learningPlan.title}</h4>
                  <p><strong>Цель:</strong> {message.learningPlan.goal}</p>
                  <p><strong>Стартовый уровень:</strong> {message.learningPlan.starting_level}</p>
                  <ol>
                    {message.learningPlan.stages.map((stage, index) => (
                      <li key={`${message.id}-stage-${index}`}>
                        <strong>{stage.title}</strong>
                        <p className="teacher-assistant-plan-topics">{stage.topics.map((item) => `${item.module}: ${item.topic}`).join(" · ")}</p>
                        <p><b>Практика:</b> {stage.practice}</p>
                        <p><b>Результат:</b> {stage.outcome}</p>
                      </li>
                    ))}
                  </ol>
                  <p><strong>Итог:</strong> {message.learningPlan.final_result}</p>
                  {message.learningPlan.assumptions.length > 0 && (
                    <p className="teacher-assistant-plan-assumptions"><strong>Допущения:</strong> {message.learningPlan.assumptions.join("; ")}</p>
                  )}
                </div>
              ) : (
                message.content
              )}
              {message.role === "assistant" && message.messageId && message.feedbackState !== "done" && (
                <div className={`teacher-assistant-feedback ${message.feedbackState === "leaving" ? "leaving" : ""}`}>
                  {message.feedbackState === "commenting" || (message.feedbackState === "saving" && message.feedbackComment !== undefined) ? (
                    <div className="teacher-assistant-feedback-form">
                      <textarea
                        value={message.feedbackComment || ""}
                        onChange={(event) => updateMessage(message.id, { feedbackComment: event.target.value.slice(0, 1000), feedbackError: "" })}
                        placeholder="Что было определено неверно?"
                        rows={3}
                        disabled={message.feedbackState === "saving"}
                      />
                      <div>
                        <button type="button" onClick={() => updateMessage(message.id, { feedbackState: "idle", feedbackComment: "", feedbackError: "" })} disabled={message.feedbackState === "saving"}>Отмена</button>
                        <button type="button" onClick={() => void sendFeedback(message, "down")} disabled={message.feedbackState === "saving" || (message.feedbackComment || "").trim().length < 3}>Отправить</button>
                      </div>
                    </div>
                  ) : (
                    <div className="teacher-assistant-feedback-actions" aria-label="Оценить ответ AI">
                      <button type="button" aria-label="Ответ полезен" onClick={() => void sendFeedback(message, "up")} disabled={message.feedbackState === "saving"}><ThumbsUp size={17} /></button>
                      <button type="button" aria-label="Ответ неточный" onClick={() => updateMessage(message.id, { feedbackState: "commenting", feedbackError: "" })} disabled={message.feedbackState === "saving"}><ThumbsDown size={17} /></button>
                    </div>
                  )}
                  {message.feedbackError && <small role="alert">{message.feedbackError}</small>}
                </div>
              )}
            </article>
          ))}
          {busy && (
            <div className="teacher-assistant-thinking" role="status">
              <span />
              <span />
              <span />
              <small>Анализирую запрос</small>
            </div>
          )}
          {error && (
            <div className="teacher-assistant-error" role="alert">
              <span>{error}</span>
              {sessionId && (
                <button type="button" onClick={() => void retry()} disabled={busy}>
                  <RefreshCw size={15} />
                  Повторить
                </button>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="teacher-assistant-composer" onSubmit={submit}>
          <div className="teacher-assistant-quick-actions" aria-label="Быстрые действия AI-помощника">
            {QUICK_ACTIONS.filter((item) => !item.curricula || curriculaReady).map((item) => (
              <button key={item.action} type="button" disabled={!sessionId || busy} onClick={() => void runQuickAction(item.action, item.message)}>{item.label}</button>
            ))}
          </div>
          <div className="teacher-assistant-composer-row">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder="Задайте вопрос о подборе преподавателя"
              rows={1}
              disabled={!sessionId || busy}
            />
            <button
              type="submit"
              aria-label="Отправить вопрос AI-помощнику"
              disabled={!draft.trim() || !sessionId || busy}
            >
              <Send size={19} />
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
